/**
 * SVG donut chart showing phase progress.
 * Each phase is a segment sized by task count, colored by status.
 * Center shows overall progress %.
 */
export default function ProgressDonut({ phases, size = 180, strokeWidth = 22, onDark = false }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const cx = size / 2;
  const cy = size / 2;

  // Total task count across all phases
  const totalTasks = phases.reduce((sum, p) => sum + p.tasks.length, 0);
  const completedTasks = phases.reduce(
    (sum, p) => sum + p.tasks.filter((t) => t.completed).length,
    0
  );
  const overallPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const statusColors = {
    completed: "#10b981",      // emerald-500
    "in-progress": "#3b82f6",  // blue-500
    blocked: "#ef4444",         // red-500
    "not-started": "rgba(255,255,255,0.08)",
  };

  // Calculate segment offsets
  let cumulative = 0;
  const segments = phases.map((phase) => {
    const weight = phase.tasks.length || 1;
    const fraction = weight / (totalTasks || 1);
    const length = fraction * circumference;
    const gap = 2; // small gap between segments
    const segment = {
      phase,
      length: Math.max(length - gap, 0),
      offset: cumulative,
      color: statusColors[phase.status] || statusColors["not-started"],
    };
    cumulative += length;
    return segment;
  });

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background ring */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth={strokeWidth}
        />
        {/* Phase segments */}
        {segments.map((seg, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${seg.length} ${circumference}`}
            strokeDashoffset={-seg.offset}
            strokeLinecap="butt"
            style={{ transition: "stroke-dasharray 0.5s ease, stroke 0.3s ease" }}
          />
        ))}
        {/* Inner glow */}
        <circle
          cx={cx}
          cy={cy}
          r={radius - strokeWidth / 2 - 2}
          fill="none"
          stroke="rgba(120, 140, 220, 0.08)"
          strokeWidth={1}
        />
      </svg>

      {/* Center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className={`text-3xl font-bold font-mono leading-none ${onDark ? "text-white" : "text-gray-200"}`}>
          {overallPct}
          <span className={`text-lg ${onDark ? "text-white/55" : "text-gray-500"}`}>%</span>
        </div>
        <div className={`text-[10px] uppercase tracking-wider font-semibold mt-1 ${onDark ? "text-white/65" : "text-gray-500"}`}>
          complete
        </div>
        <div className={`text-[10px] font-mono mt-0.5 ${onDark ? "text-white/45" : "text-gray-600"}`}>
          {completedTasks} / {totalTasks}
        </div>
      </div>
    </div>
  );
}
